import { terms } from '../../language/terms';
import { resolveAvatarUrl } from '../../utility/azure-blob-fetch';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import type { SlSwitch } from '@shoelace-style/shoelace';
import styles from './profile-styles.css?inline';
import { styles as sharedStyles } from '../../styles/shared-styles';

// Updated Shoelace component imports
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/tab-group/tab-group.js';
import '@shoelace-style/shoelace/dist/components/tab/tab.js';
import '@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';

// Import the component dialogs
import './two-factor-auth-dialog.js';
import './password-change-request-dialog.js';
import '../../components/image-picker/image-picker-modal.js';
import './app-profile-features.js';

import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';
import { TwoFactorAuthDialog } from './two-factor-auth-dialog';
import { PasswordChangeRequestDialog } from './password-change-request-dialog';
import { ImagePickerModal } from '../../components/image-picker/image-picker-modal';
import { ApiFactory } from '../../api/agwasuite-api-factory';
import { ApiLogin } from '../../api/core/agwasuite-api-login';
import { userDirectoryInstance } from '../../global-data/user-directory-instance';
import { awaitableTemplate } from '../../utility/awaitable-data';

@customElement('app-profile')
export class AppProfile extends WithUserProfileConsumer(LitElement) {
  static styles = [sharedStyles, unsafeCSS(styles)];

  @query('two-factor-auth-dialog')
  private twofaDialog!: TwoFactorAuthDialog;

  @query('password-change-request-dialog')
  private passwordChangeDialog!: PasswordChangeRequestDialog;

  @query('image-picker-modal')
  private imagePickerModal!: ImagePickerModal;

  /** Unsubscribe function for user directory updates */
  private _userDirectoryUnsubscribe?: () => void;

  /** Currently subscribed user ID for cache updates */
  private _subscribedUserId?: string;

  connectedCallback() {
    super.connectedCallback();
    this._subscribeToUserUpdates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._userDirectoryUnsubscribe?.();
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    // Re-subscribe if user changes
    this._subscribeToUserUpdates();
  }

  /** Subscribe to user directory updates for the current user */
  private _subscribeToUserUpdates() {
    const userId = this.userProfile?.data?.userId;
    if (userId === this._subscribedUserId) return; // already subscribed to this user

    // Unsubscribe from previous user
    this._userDirectoryUnsubscribe?.();
    this._userDirectoryUnsubscribe = undefined;
    this._subscribedUserId = undefined;

    if (!userId) return;

    // Subscribe to updates for the current user
    const cache = userDirectoryInstance();
    this._userDirectoryUnsubscribe = cache.subscribe(userId, () => {
      this.requestUpdate();
    });
    this._subscribedUserId = userId;
  }

  /**
   * Get profile image URL from UserDirectory cache (async)
   */
  private async getProfileImageUrl(): Promise<string> {
    const userId = this.userProfile?.data?.userId;
    if (!userId) return '/assets/images/profile-default.png';
    const cache = userDirectoryInstance();
    const user = await cache.getUserDto(userId);
    if (user?.profileImageUrl) {
      // SAS URLs from blob storage are absolute, relative paths need domain prefix
      // Through the shared resolver: a value here can be an absolute proxy URL, an API-relative
      // path, or a data URL for an avatar that was just uploaded and has not been refetched.
      return resolveAvatarUrl(user.profileImageUrl)!;
    }
    return '/assets/images/profile-default.png';
  }

  private openTwoFactorSetup() {
    void this.twofaDialog.open();
  }

  private openPasswordChangeDialog() {
    this.passwordChangeDialog.open();
  }

  private openImagePicker() {
    this.imagePickerModal.open();
  }

  private async handleImageSelected(e: CustomEvent) {
    const imageBase64 = e.detail.imageBase64;
    if (!imageBase64) {
      console.error('No image data received');
      return;
    }

    try {
      const api = ApiFactory.authenticated(ApiLogin);
      const result = await api.updateProfilePicture(imageBase64);

      if (result.success) {
        // Instead of updating directly, dispatch an event to be handled by app-index
        this.dispatchEvent(
          new CustomEvent('profile-image-updated', {
            detail: { imageBase64 },
            bubbles: true,
            composed: true,
          })
        );
      } else {
        console.error('Failed to update profile picture:', result.error);
        // You might want to show an error message to the user
      }
    } catch (err) {
      console.error('Error updating profile picture:', err);
    }
  }

  private handleTwoFactorChange(e: Event) {
    // Prevent default action to avoid switch state change
    e.preventDefault();
    this.openTwoFactorSetup();
    // Return checked to original value
    const switchEl = e.target as SlSwitch;
    switchEl.checked = this.userProfile?.isTwoFactorEnabled ?? false;
  }

  private handleUserLogout() {
    this.dispatchEvent(new CustomEvent('logout'));
  }

  render() {
    const profileImage = awaitableTemplate(async () => {
      const imgurl = await this.getProfileImageUrl();
      return html`
        <div class="profile-image-container">
          <img slot="image" src=${imgurl} alt="Your avatar" />
          <sl-icon-button
            name="pencil-square"
            label="Change profile picture"
            class="edit-profile-image"
            @click=${this.openImagePicker}
          ></sl-icon-button>
        </div>
      `;
    });

    // Get group name from claims if present and not empty
    const groupName = this.userProfile?.access.groupName;

    return html`
      ${
        this.userProfile
          ? html`
              <sl-card>
                ${profileImage}
                <h2>Your Profile Information</h2>
                <div class="profile-info-grid">
                  <div class="profile-info-label">Username:</div>
                  <div class="profile-info-value">
                    ${this.userProfile.username}
                  </div>

                  <div class="profile-info-label">Name:</div>
                  <div class="profile-info-value">
                    ${this.userProfile.displayName}
                  </div>

                  <div class="profile-info-label">Email:</div>
                  <div class="profile-info-value">
                    ${this.userProfile.emailAddress}
                  </div>

                  ${
                    groupName
                      ? html`
                          <div class="profile-info-label">
                            ${terms.loginGroup.value}
                          </div>
                          <div class="profile-info-value">${groupName}</div>
                        `
                      : null
                  }
                </div>
              </sl-card>

              <div class="tabs-container">
                <sl-tab-group placement="start">
                  <sl-tab slot="nav" panel="account-security"
                    >Account Security</sl-tab
                  >
                  <sl-tab slot="nav" panel="features">Features</sl-tab>

                  <sl-tab-panel name="account-security">
                    <sl-card>
                      <h3>Account Security Settings</h3>
                      <p>
                        Manage your account security preferences and settings
                        here.
                      </p>
                      <p>Last login: ${new Date().toLocaleDateString()}</p>

                      <div class="security-options">
                        ${
                          this.userProfile.isInternal
                            ? html` <sl-tooltip
                                content="For security reasons, to disable two-factor authentication please contact your administrator who will remove 2FA and issue a password reset. After a reset, all existing Authenticator apps will no longer work and must be re-added with the new security settings."
                                placement="right"
                                ?disabled=${!this.userProfile.isTwoFactorEnabled}
                              >
                                <div class="security-option-row">
                                  <sl-switch
                                    ?checked=${this.userProfile.isTwoFactorEnabled}
                                    ?disabled=${this.userProfile.isTwoFactorEnabled}
                                    @sl-change=${this.handleTwoFactorChange}
                                    >Enable two-factor authentication</sl-switch
                                  >
                                  ${
                                    this.userProfile.isTwoFactorEnabled
                                      ? html`<sl-button
                                          size="small"
                                          @click=${this.openTwoFactorSetup}
                                          >Add Authenticator</sl-button
                                        >`
                                      : null
                                  }
                                </div>
                              </sl-tooltip>`
                            : null
                        }
                        <sl-tooltip
                          content="This feature is not enabled yet."
                          placement="right"
                        >
                          <sl-switch checked disabled
                            >Email notifications for login attempts</sl-switch
                          >
                        </sl-tooltip>
                      </div>

                      <sl-button
                        variant="primary"
                        class="change-password-btn"
                        @click=${this.openPasswordChangeDialog}
                      >
                        Change Password
                      </sl-button>
                    </sl-card>
                  </sl-tab-panel>

                  <sl-tab-panel name="features">
                    <app-profile-features
                      .userProfile=${this.userProfile}
                    ></app-profile-features>
                  </sl-tab-panel>
                </sl-tab-group>
              </div>

              <!-- Two-Factor Authentication Setup Dialog Component -->
              ${
                this.userProfile.isInternal
                  ? html`<two-factor-auth-dialog
                      username=${this.userProfile.username}
                      @verification-success=${this.on2faSuccess}
                    ></two-factor-auth-dialog>`
                  : null
              }

              <!-- Password Change Request Dialog Component -->
              <password-change-request-dialog
                @logout-requested=${this.handleUserLogout}
              ></password-change-request-dialog>

              <!-- Image Picker Modal Component -->
              <image-picker-modal
                dialogTitle="Change Profile Picture"
                aspectRatio="1"
                maxWidth="300"
                maxHeight="300"
                acceptedFileTypes="image/jpeg, image/png, image/webp"
                @image-selected=${this.handleImageSelected}
              ></image-picker-modal>
            `
          : html`<p>Please log in</p>`
      }
    `;
  }

  on2faSuccess() {
    this.userProfile!.isTwoFactorEnabled = true;
    this.requestUpdate();
  }
}
